import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, KeyRound, Crown } from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function AdminUsersPage() {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form state
  const [form, setForm] = useState({
    username: '', full_name: '', email: '', password: '', role_id: '', is_super_admin: false,
  });
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        axios.get(`${API}/admin-users`, auth()),
        axios.get(`${API}/roles`, auth()),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openNewUser = () => {
    setEditingUser(null);
    setForm({ username: '', full_name: '', email: '', password: '', role_id: '', is_super_admin: false });
    setShowUserDialog(true);
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setForm({
      username: u.username,
      full_name: u.full_name || '',
      email: u.email || '',
      password: '',
      role_id: u.role_id || '',
      is_super_admin: u.is_super_admin || false,
    });
    setShowUserDialog(true);
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      if (editingUser) {
        const updates = { full_name: form.full_name, email: form.email, role_id: form.role_id };
        if (currentUser?.is_super_admin) updates.is_super_admin = form.is_super_admin;
        await axios.patch(`${API}/admin-users/${editingUser.id}`, updates, auth());
        toast.success('User updated');
      } else {
        const payload = {
          username: form.username,
          full_name: form.full_name,
          email: form.email,
          password: form.password,
          role_id: form.role_id,
          is_super_admin: form.is_super_admin,
        };
        await axios.post(`${API}/admin-users`, payload, auth());
        toast.success('User created');
      }
      setShowUserDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin-users/${passwordTarget.id}/reset-password`, { new_password: newPassword }, auth());
      toast.success('Password updated');
      setShowPasswordDialog(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/admin-users/${deleteTarget.id}`, auth());
      toast.success('User deleted');
      setShowDeleteDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await axios.patch(`${API}/admin-users/${u.id}`, { is_active: !u.is_active }, auth());
      toast.success(`User ${!u.is_active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const formatDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString(); } catch { return val; }
  };

  const getRoleBadge = (roleName) => {
    if (!roleName) return <span className="text-xs text-muted-foreground">No role</span>;
    const colors = {
      super_admin: 'bg-red-100 text-red-800',
      'Admissions Manager': 'bg-blue-100 text-blue-800',
      'Reviewer': 'bg-amber-100 text-amber-800',
      'Read Only': 'bg-gray-100 text-gray-700',
    };
    const cls = colors[roleName] || 'bg-purple-100 text-purple-800';
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{roleName}</span>;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-playfair font-bold text-primary">Admin Users</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage portal user accounts and their role assignments</p>
        </div>
        <Button onClick={openNewUser} className="btn-primary gap-2">
          <Plus className="w-4 h-4" /> New User
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading users...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Full Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-1.5">
                        {u.is_super_admin && <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                        {u.username}
                      </div>
                    </TableCell>
                    <TableCell>{u.full_name || '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email || '—'}</TableCell>
                    <TableCell>{getRoleBadge(u.role_name)}</TableCell>
                    <TableCell>
                      <button onClick={() => handleToggleActive(u)} title="Toggle active">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {u.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </button>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(u.last_login)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" title="Edit" onClick={() => openEditUser(u)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Reset Password" onClick={() => { setPasswordTarget(u); setNewPassword(''); setShowPasswordDialog(true); }}>
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" title="Delete"
                          disabled={u.id === currentUser?.id}
                          onClick={() => { setDeleteTarget(u); setShowDeleteDialog(true); }}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No users found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'New Admin User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editingUser && (
              <div className="space-y-1.5">
                <Label htmlFor="u-username">Username <span className="text-red-500">*</span></Label>
                <Input id="u-username" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="e.g. john_doe" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="u-fullname">Full Name <span className="text-red-500">*</span></Label>
              <Input id="u-fullname" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="John Doe" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="u-email">Email</Label>
              <Input id="u-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="john@example.com" />
            </div>
            {!editingUser && (
              <div className="space-y-1.5">
                <Label htmlFor="u-password">Password <span className="text-red-500">*</span></Label>
                <PasswordInput id="u-password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min. 6 characters" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="u-role">Role</Label>
              <select
                id="u-role"
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={form.role_id}
                onChange={e => setForm(f => ({ ...f, role_id: e.target.value }))}
              >
                <option value="">— No role —</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            {currentUser?.is_super_admin && (
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_super_admin}
                  onCheckedChange={v => setForm(f => ({ ...f, is_super_admin: v }))}
                  id="u-superadmin"
                />
                <Label htmlFor="u-superadmin">Super Admin</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleSaveUser} disabled={saving}>
              {saving ? 'Saving...' : editingUser ? 'Save Changes' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Set a new password for <strong>{passwordTarget?.username}</strong>.</p>
            <div className="space-y-1.5">
              <Label htmlFor="new-pw">New Password</Label>
              <PasswordInput
                id="new-pw"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Min. 6 characters"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleResetPassword} disabled={saving}>
              {saving ? 'Updating...' : 'Update Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete <strong>{deleteTarget?.username}</strong>? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
